import React, { useMemo, useState } from 'react';
import { useGetIdentity, useMenu } from '@refinedev/core';
import { useLocation } from 'react-router';
import type { IERPUser } from '@admin-panel/types';
import { WarehouseFacilitySelector, VendorSelector } from '../header';
import { BiohazardIcon, DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { PORTAL_NAME } from '@admin-panel/config/env';
import { Drawer, Space, Spin, Typography } from 'antd';
import Sider from '../Sider';
import * as Styles from './styles';
import { PageHeaderProvider, usePageHeaderState } from './page-header-context';
import { HeaderToolsProvider, useHeaderTools } from './header-tools-context';
import { useMobileLayout } from './useMobileLayout';
import { useGlobalContext } from '@admin-panel/hooks';
import { FacilityFirstPick } from '../header/FacilityPicker';

type LayoutProps = {
  children: React.ReactNode;
  /** Controls shown in the top bar on every page (see header-tools-context). */
  headerTools?: React.ReactNode;
};

const isLocalConnectedToProduction =
  import.meta.env['VITE_APP_ENV'] === 'local' &&
  import.meta.env['VITE_GATEWAY_API_URL'] === 'https://gateway.lyskraft.in/api/admin';

/** Pages reached from a menu item that live under another path. */
const PAGE_ALIASES: Array<[string, string]> = [
  ['/bulk-pick', '/picklists'],
  ['/on-demand-pick', '/picklists'],
];

const Layout = ({ children, headerTools = null }: LayoutProps) => {
  const { menuItems, selectedKey } = useMenu();
  const { data: user } = useGetIdentity<IERPUser>();
  const isMobile = useMobileLayout();
  const { facilityChosen } = useGlobalContext();
  const askFacility = isMobile && PORTAL_NAME === 'warehouse' && !!user && !facilityChosen;

  const currentMenuItem = useMemo(() => {
    const traverse = (items: any[]): any | null => {
      for (const item of items) {
        if (item.key === selectedKey) return item;
        if (item.children) {
          const found = traverse(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return traverse(menuItems);
  }, [menuItems, selectedKey]);

  // A page that is not itself a menu item (a create form, a pick screen) takes the title of the menu item it belongs
  // to: the longest menu route its path starts with, or an alias. Only the home page is "Dashboard".
  const location = useLocation();
  const parentMenuItem = useMemo(() => {
    if (currentMenuItem) return undefined;
    const path = PAGE_ALIASES.find(([prefix]) => location.pathname.startsWith(prefix))?.[1] ?? location.pathname;
    let best: any = null;
    const traverse = (items: any[]) => {
      for (const item of items) {
        const route: string | undefined = item.route ?? item.list;
        if (route && route !== '/' && path.startsWith(route) && (!best || route.length > (best.route ?? best.list).length)) {
          best = item;
        }
        if (item.children) traverse(item.children);
      }
    };
    traverse(menuItems);
    return best;
  }, [currentMenuItem, menuItems, location.pathname]);

  const pageTitle =
    currentMenuItem?.label ||
    currentMenuItem?.name ||
    parentMenuItem?.label ||
    (location.pathname === '/' ? 'Dashboard' : '');

  return (
    <HeaderToolsProvider value={headerTools}>
      <PageHeaderProvider>
        {isLocalConnectedToProduction && <EnvironmentalHazard />}
        <Styles.LayoutRoot>
          <Sider mobileTitle={pageTitle} />
          <Styles.PageArea $isMobile={isMobile}>
            {!isMobile && <HeaderBar defaultTitle={pageTitle} user={user} isDesktop={!isMobile} />}

            <Styles.ContentWrapper $isMobile={isMobile}>
              {askFacility ? (
                <FacilityFirstPick user={user} />
              ) : (
                <>
                  {/* Actions first: the sticky pill then overlays the title row's free right side, never the
                      page's first content (e.g. Hub's alert strip). Hub shows the title in the top bar, so there
                      the actions take their own row. */}
                  {isMobile && <MobileActions inFlow={PORTAL_NAME === 'warehouse'} />}
                  {isMobile && PORTAL_NAME !== 'warehouse' && <MobilePageTitle defaultTitle={pageTitle} />}
                  {children}
                </>
              )}
            </Styles.ContentWrapper>
          </Styles.PageArea>
        </Styles.LayoutRoot>
        {/* <Toaster /> */}
      </PageHeaderProvider>
    </HeaderToolsProvider>
  );
};

const TOP_ACTIONS_LIMIT = 4;
/**
 * One page-title style on every width (U11): sentence case as written ("Package #2865"), never
 * upper-cased on desktop only. Phones get a slightly smaller size, nothing else changes.
 */
export const PageTitle = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <Typography.Title
    level={3}
    className="font-gloock lk-page-title"
    style={{ fontWeight: 700, overflowWrap: 'anywhere', ...style }}
  >
    {children}
  </Typography.Title>
);
const MobilePageTitle = ({ defaultTitle }: { defaultTitle: React.ReactNode }) => {
  const { title, actions, isLoading } = usePageHeaderState();
  // Leave room for the Actions pill that sits over this row.
  const hasActions = Boolean(actions) || isLoading;
  return (
    <PageTitle style={{ margin: hasActions ? '12px 152px 12px 16px' : '12px 16px', fontSize: 22 }}>
      {title ?? defaultTitle}
    </PageTitle>
  );
};

type HeaderBarProps = {
  defaultTitle: string;
  user?: IERPUser;
  isDesktop: boolean;
};

const HeaderBar = ({ defaultTitle, user, isDesktop }: HeaderBarProps) => {
  const isMobile = !isDesktop;
  const { title, actions, isLoading } = usePageHeaderState();
  const headerTools = useHeaderTools();
  const effectiveTitle = title ?? defaultTitle;
  const hasPortalSelectors = PORTAL_NAME === 'warehouse' || PORTAL_NAME === 'vendor';
  const computeActions = () => {
    if (!actions) {
      return {
        topActions: null as React.ReactNode,
        bottomActions: null as React.ReactNode,
      };
    }

    if (hasPortalSelectors) {
      return {
        topActions: null,
        bottomActions: actions,
      };
    }

    const nodesArray = React.Children.toArray(actions);

    if (nodesArray.length === 1) {
      const singleNode = nodesArray[0];

      if (React.isValidElement(singleNode) && singleNode.props?.children) {
        const childArray = React.Children.toArray(singleNode.props.children);

        if (childArray.length > TOP_ACTIONS_LIMIT) {
          const topChildNodes = childArray.slice(0, TOP_ACTIONS_LIMIT);
          const bottomChildNodes = childArray.slice(TOP_ACTIONS_LIMIT);

          const topElement = React.cloneElement(
            singleNode,
            {
              key: singleNode.key ?? 'top-actions',
            },
            topChildNodes,
          );
          const bottomElement = React.cloneElement(
            singleNode,
            {
              key: singleNode.key ? `${singleNode.key}-overflow` : 'bottom-actions',
            },
            bottomChildNodes,
          );

          return {
            topActions: topElement,
            bottomActions: bottomElement,
          };
        }
      }
    }

    if (nodesArray.length <= TOP_ACTIONS_LIMIT) {
      return {
        topActions: nodesArray,
        bottomActions: null,
      };
    }

    return {
      topActions: nodesArray.slice(0, TOP_ACTIONS_LIMIT),
      bottomActions: nodesArray.slice(TOP_ACTIONS_LIMIT),
    };
  };

  const { topActions, bottomActions } = computeActions();

  return (
    <>
      <Styles.HeaderBar $isMobile={isMobile}>
        <Styles.Top $isMobile={isMobile}>
          <PageTitle style={{ margin: 0, width: 'fit-content' }}>{effectiveTitle}</PageTitle>
          <Space
            size={12}
            align="center"
            style={{ justifyContent: 'flex-end', flex: 1, flexWrap: 'wrap' }}
            data-testid="top-page-actions"
          >
            {isLoading && <Spin size="small" />}
            {headerTools}
            {PORTAL_NAME === 'warehouse' && <WarehouseFacilitySelector user={user} />}
            {PORTAL_NAME === 'vendor' && <VendorSelector user={user} />}
            {topActions}
          </Space>
        </Styles.Top>
      </Styles.HeaderBar>
      {bottomActions && (
        <Styles.Bottom data-testid="bottom-page-actions">{bottomActions}</Styles.Bottom>
      )}
    </>
  );
};

const MobileActions = ({ inFlow = false }: { inFlow?: boolean }) => {
  const { actions, isLoading } = usePageHeaderState();
  const [actionsOpen, setActionsOpen] = useState(false);

  const shouldRender = Boolean(actions) || isLoading;
  if (!shouldRender) return null;

  return (
    <Styles.MobileActionsBar $inFlow={inFlow}>
      {!actions && isLoading && (
        <Styles.MobileActionsLoading>
          <Spin size="small" />
        </Styles.MobileActionsLoading>
      )}
      {actions && (
        <>
          <Styles.MobileActionsTrigger
            size="large"
            loading={isLoading}
            icon={<DotsThreeVerticalIcon size={18} weight="bold" />}
            onClick={() => setActionsOpen(true)}
            aria-label="Open page actions"
          >
            Actions
          </Styles.MobileActionsTrigger>
          <Drawer
            title={
              <Styles.MobileActionsSheetTitle>
                <span>Page actions</span>
                <small>Choose the action you need for this page</small>
              </Styles.MobileActionsSheetTitle>
            }
            placement="bottom"
            open={actionsOpen}
            onClose={() => setActionsOpen(false)}
            height="auto"
            styles={{
              mask: { background: 'rgba(15, 23, 42, 0.24)' },
              content: {
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                maxHeight: '82dvh',
                overflow: 'hidden',
              },
              header: { padding: '18px 20px 12px', borderBottom: 'none' },
              body: {
                padding: '4px 20px 20px',
                maxHeight: 'calc(82dvh - 76px)',
                overflowY: 'auto',
              },
            }}
          >
            <Styles.MobileActionsSheetHandle aria-hidden="true" />
            <Styles.MobileActionsContent>{actions}</Styles.MobileActionsContent>
          </Drawer>
        </>
      )}
    </Styles.MobileActionsBar>
  );
};

const EnvironmentalHazard = () => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        zIndex: 9999,
        width: '100%',
        background: '#FF0000',
        height: '2px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <Typography.Text
        style={{
          background: '#FF0000',
          color: '#ffffff',
          fontSize: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '0 6px',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <BiohazardIcon>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            dur="3s"
            from="0 0 0"
            to="360 0 0"
            repeatCount="indefinite"
          ></animateTransform>
        </BiohazardIcon>
        localhost is connected to production
      </Typography.Text>
    </div>
  );
};

export default Layout;
